// The fulfillment section's field set, extracted from OrderServicesForm.tsx so a
// future standalone Fulfillment door reuses it rather than forking a second copy
// (ADR-015 §6 — the same move phase 4b made for KittingFields).
//
// Every item on a fulfilled order is replenished into stock under its own SKU, so
// the section asks one thing per item — which SKU does it go into? — plus the
// barcode gate. Six printed items, two of them built into kits, are six SKUs: each
// print no kit consumes, plus each assembly. The answer is stored on the item's
// own line (`config.fulfillment_target`) and read back through the shared
// resolver the submit gate and the Nexus feeds use, so the row says exactly what
// will be transmitted:
//
//   print a kit on the order consumes → goes into that kit, unless remapped
//   print a book takes as an insert   → part of that book, unless remapped
//   assembly                          → its own kit's SKU (or, per unit, the SKUs
//                                        the stock came from; with no kit, a new
//                                        SKU sales sets up), unless remapped or
//                                        made part of another item on the order
//   any other print                   → must be answered: a SKU we already
//                                        stock for them, a new one they name, or
//                                        part of another item on the order (no
//                                        SKU of its own)
//
// The agreement boolean from Nexus governs the COPY around the question, never
// whether it is asked. No agreement adds one sentence saying sales will set the
// plan up; the answers are captured as intent either way.
//
// Nothing here is priced. Once fulfillment is in play the recurring side is
// Argo's to set up, not the portal's to quote — the section captures intent and
// transmits it, exactly like every other order service.
//
// ⚠️ The no-agreement branch is ALSO what a Nexus outage produces (the lookup
// degrades to false). So its copy says what happens next and never asserts "you
// have no agreement with us" as a fact about Argo's records — that sentence
// would be a lie to a real customer during an outage.

import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

import type { TFunction } from 'i18next';

import {
  BARCODE_RESOLUTIONS,
  BARCODE_RESOLUTION_LABELS,
  FULFILLMENT_TARGET_CHOICE_LABELS,
  configFulfillmentTarget,
  fulfillmentHostLeadsBack,
  fulfillmentTargetFieldKey,
  isConfiguredLine,
  resolveLineFulfillmentTarget,
  type FulfilledSku,
  type FulfilledSkuSnapshot,
  type FulfillmentLineRef,
  type FulfillmentSnapshot,
  type LineFulfillmentTarget,
  type ResolvedFulfillmentTarget,
} from 'shared';

import { getCompanyFulfillmentStatus } from '../api/fulfillment-status.js';
import { useApplyBarcodeWording } from './artworkBarcode.js';
import { readBackValue } from './FieldReadBack.js';
import { withLineFulfillmentTarget } from './fulfillmentTargets.js';
import { SearchableSelect, type SearchableOption } from './SearchableSelect.js';
import { EnumRadios } from './KittingFields.js';
import { localizeVocabLabels } from './vocabLabels.js';
import { useWarehouseRefresh } from './useWarehouseRefresh.js';

// One item on the order as the fulfillment section reads it.
export type FulfillmentItem = {
  // The line's id — its QuoteLine id once saved — which is also the id the
  // submit gate names in its `fulfillment.target.<id>.*` error keys.
  id: string;
  // False for a line added since the last save. Its id is a synthetic local one
  // that the save replaces, so another item's `with_line_id` must not point at it.
  saved: boolean;
  // What the item list calls it, and the "Item 2 · Paperback book" caption the
  // review row prints above it.
  label: string;
  caption: string;
  config: unknown;
  title?: string | null;
  // A starting name for a new SKU: the item's own title or derived name, never
  // the positional "Item 3" fallback.
  suggestedSkuName?: string;
};

function fulfilledSkuSnapshot(sku: FulfilledSku): FulfilledSkuSnapshot {
  return { name: sku.name, kind: sku.kind, component_count: sku.component_count };
}

// One SKU as a picker row. Every SKU Nexus returns is offered; an inactive one is
// tagged in the row's hint — the same muted line CompanyItemPicker tags it with —
// so it reads apart from a live SKU of the same name. Searchable by SKU as well
// as name: the SKU is not in the label's leading position here, so it rides as a
// keyword.
function fulfilledSkuOption(sku: FulfilledSku, t: TFunction): SearchableOption {
  return {
    value: sku.sku,
    label: `${sku.name} (${sku.sku})`,
    ...(sku.status === 'inactive' ? { hint: t('quote:stockPicker.inactive') } : {}),
    keywords: [sku.sku, sku.name],
  };
}

function skuText(sku: string, name: string | undefined): string {
  return name ? `${name} (${sku})` : sku;
}

type FulfillmentFieldsProps = {
  fulfillment: FulfillmentSnapshot;
  onPatchFulfillment: (patch: Partial<FulfillmentSnapshot>) => void;
  // Every active item on the order, in page order.
  items: readonly FulfillmentItem[];
  // `undefined` restores the item's default (see withLineFulfillmentTarget).
  onPatchItemTarget?: (id: string, target: LineFulfillmentTarget | undefined) => void;
  errors: Record<string, string>;
  disabled?: boolean;
};

export function FulfillmentFields({
  fulfillment,
  onPatchFulfillment,
  items,
  onPatchItemTarget,
  errors,
  disabled,
}: FulfillmentFieldsProps): JSX.Element {
  const { t } = useTranslation(['quote', 'common']);

  // Non-price company data, so R4 would permit a cache, but none is held: the
  // section revalidates against Nexus on every mount, so a SKU that reached Nexus
  // is offered the next time it opens.
  const { data: status } = useQuery({
    queryKey: ['company-fulfillment-status'],
    queryFn: () => getCompanyFulfillmentStatus(),
    enabled: !disabled,
    // Zero, overriding the app-wide default; not on window focus, as the item
    // pickers.
    staleTime: 0,
    refetchOnWindowFocus: false,
  });
  const hasAgreement = status?.has_fulfillment_agreement === true;
  const skus: FulfilledSku[] = status?.skus ?? [];

  // One list feeds every row's picker, so one refresh serves them all. A SKU
  // added in ArgoWMS since Nexus's last scheduled pull is what it is for.
  const { refreshing, refresh } = useWarehouseRefresh({
    queryKey: ['company-fulfillment-status'],
    fetch: () => getCompanyFulfillmentStatus({ refreshFromWarehouse: true }),
  });
  const skuRefresh =
    status?.has_company === true
      ? {
          label: t('stockPicker.refresh'),
          busyLabel: t('stockPicker.refreshing'),
          busy: refreshing,
          onRefresh: refresh,
        }
      : undefined;

  // The paid answer says what Argo will do on THIS order, worded by the same
  // hook as the Shipping/Pickup add-on.
  const applyBarcode = useApplyBarcodeWording(items.map((i) => i.config));
  const barcodeLabels = {
    ...localizeVocabLabels('orderServices.fulfillment.barcodeOptions', BARCODE_RESOLUTION_LABELS, t),
    apply_barcode: applyBarcode.label,
  };

  const lines: FulfillmentLineRef[] = items.map((i) => ({
    id: i.id,
    config: i.config,
    title: i.title ?? null,
  }));

  const legendId = 'fulfillment.targets-legend';
  return (
    <>
      <fieldset className="field-group" aria-labelledby={legendId}>
        <legend id={legendId} className="field-group__legend">
          {t('orderServices.fulfillment.targets.legend')}
        </legend>
        {!disabled && (
          <span className="help help--block">{t('orderServices.fulfillment.targets.help')}</span>
        )}
        {/* Says what happens next WITHOUT asserting "you have no agreement" —
            a Nexus outage degrades the lookup to false and produces this exact
            branch for a customer who does have one. */}
        {!hasAgreement && !disabled && (
          <p className="help help--flush">{t('orderServices.fulfillment.noAgreementNote')}</p>
        )}
        <ul className="fulfillment-targets">
          {items.map((item) => (
            <FulfillmentTargetRow
              key={item.id}
              item={item}
              items={items}
              lines={lines}
              fulfillment={fulfillment}
              skus={skus}
              skuRefresh={skuRefresh}
              statusLoaded={status !== undefined}
              onPatchItemTarget={onPatchItemTarget}
              errors={errors}
              disabled={disabled}
            />
          ))}
        </ul>
      </fieldset>

      {/* Item 6b — mandatory-barcode question for fulfilled orders. A picked
          order is pulled from inventory by scan, so the artwork must carry a
          barcode. Auto-satisfied at the submit gate when the upload scan
          already found one, so it's safe to skip when your files include a
          barcode — but the customer can confirm or opt into "apply one" here. */}
      <EnumRadios
        fieldKey="fulfillment.barcode_resolution"
        legend={t('orderServices.fulfillment.barcodeLegend')}
        help={t('orderServices.fulfillment.barcodeHelp')}
        options={BARCODE_RESOLUTIONS}
        labels={barcodeLabels}
        optionHelp={{ apply_barcode: applyBarcode.help }}
        value={fulfillment.barcode_resolution}
        onPick={(v) => onPatchFulfillment({ barcode_resolution: v })}
        errors={errors}
        disabled={disabled}
      />
    </>
  );
}

// The row's control value — a UI state, never stored. '' is the item's default;
// 'unset' is the "Choose…" placeholder of a row with nothing to go on; 'existing'
// / 'new' are the two answers a customer can store. The stored answer wins, so a
// half-finished "existing" with no SKU picked yet keeps its picker open;
// otherwise the resolved outcome decides, which is how a legacy order-wide answer
// still shows on the rows it covers. An explicit 'into_kit' reads as the default
// it restates while it is true, and as unanswered once it is not (the gate then
// reports the row), so the default stays a real change the customer can pick.
// 'with_item' is stored like 'existing': a pick with its item still to choose
// keeps its picker open.
type RowChoice = '' | 'unset' | 'existing' | 'new' | 'with_item';

function rowChoice(
  stored: LineFulfillmentTarget | undefined,
  resolved: ResolvedFulfillmentTarget,
): RowChoice {
  if (stored?.choice === 'existing' || stored?.choice === 'new' || stored?.choice === 'with_item') {
    return stored.choice;
  }
  if (resolved.kind === 'existing_sku') return 'existing';
  if (resolved.kind === 'new_sku') return 'new';
  return resolved.kind === 'unanswered' ? 'unset' : '';
}

function FulfillmentTargetRow({
  item,
  items,
  lines,
  fulfillment,
  skus,
  skuRefresh,
  statusLoaded,
  onPatchItemTarget,
  errors,
  disabled,
}: {
  item: FulfillmentItem;
  items: readonly FulfillmentItem[];
  lines: readonly FulfillmentLineRef[];
  fulfillment: FulfillmentSnapshot;
  skus: FulfilledSku[];
  // The "check the warehouse" action, or undefined where there is no Nexus
  // Company to refresh.
  skuRefresh:
    | { label: string; busyLabel: string; busy: boolean; onRefresh: () => void }
    | undefined;
  statusLoaded: boolean;
  onPatchItemTarget?: (id: string, target: LineFulfillmentTarget | undefined) => void;
  errors: Record<string, string>;
  disabled?: boolean;
}): JSX.Element {
  const { t } = useTranslation(['quote', 'common']);
  const ctx = { lines, orderFulfillment: fulfillment };
  const resolved = resolveLineFulfillmentTarget(
    { id: item.id, config: item.config, title: item.title ?? null },
    ctx,
  );
  // What the item does with no answer of its own — the line resolved with its
  // stored target set aside. Its kind decides the default option's wording.
  const fallback = resolveLineFulfillmentTarget(
    { id: item.id, config: withoutTarget(item.config), title: item.title ?? null },
    ctx,
  );
  const stored = configFulfillmentTarget(item.config);
  const choice = rowChoice(stored, resolved);
  const ownKitSku = fallback.kind === 'kit_own_sku' ? fallback.sku : undefined;

  const choiceLabels = localizeVocabLabels(
    'orderServices.fulfillment.targets.choiceOptions',
    FULFILLMENT_TARGET_CHOICE_LABELS,
    t,
  );

  function itemLabels(ids: readonly string[]): string {
    return ids.map((id) => items.find((i) => i.id === id)?.label ?? id).join(', ');
  }

  // The default, said as the item's own outcome rather than as "Default".
  function defaultLabel(): string | null {
    switch (fallback.kind) {
      case 'into_kit':
        return t('orderServices.fulfillment.targets.intoKit', {
          kits: itemLabels(fallback.kit_line_ids),
        });
      case 'with_item':
        return t('orderServices.fulfillment.targets.withItem', {
          items: itemLabels(fallback.host_line_ids),
        });
      case 'kit_own_sku':
        return t('orderServices.fulfillment.targets.ownSku', {
          target: skuText(fallback.sku, fallback.name),
        });
      case 'kit_stock_skus':
        return t('orderServices.fulfillment.targets.stockSkus');
      case 'kit_new_sku':
        return t('orderServices.fulfillment.targets.kitNewSku');
      default:
        return null;
    }
  }

  // The same outcome as one read-back sentence, for a submitted order.
  function readBack(): string {
    switch (resolved.kind) {
      case 'existing_sku':
        return skuText(resolved.sku, resolved.sku_snapshot?.name);
      case 'new_sku':
        return t('orderServices.fulfillment.targets.newNamed', { name: resolved.new_sku_name });
      case 'with_item':
        return t('orderServices.fulfillment.targets.withItem', {
          items: itemLabels(resolved.host_line_ids),
        });
      case 'unanswered':
        return readBackValue(undefined);
      default:
        return defaultLabel() ?? readBackValue(undefined);
    }
  }

  const choiceId = fulfillmentTargetFieldKey(item.id, 'choice');
  const skuId = fulfillmentTargetFieldKey(item.id, 'sku');
  const nameId = fulfillmentTargetFieldKey(item.id, 'new_sku_name');
  const withId = fulfillmentTargetFieldKey(item.id, 'with_line_id');
  const choiceError = errors[choiceId];
  const skuError = errors[skuId];
  const nameError = errors[nameId];
  const withError = errors[withId];

  const caption = (
    <span className="fulfillment-target__kind">{item.caption}</span>
  );

  if (disabled || !onPatchItemTarget) {
    return (
      <li className="fulfillment-target">
        <div className="fulfillment-target__item">
          {caption}
          <span className="fulfillment-target__name">{item.label}</span>
        </div>
        <p className="value--locked fulfillment-target__control">{readBack()}</p>
      </li>
    );
  }
  const patch = onPatchItemTarget;

  const defaultOption = defaultLabel();
  // A new SKU sales sets up is already this assembly's default; a second "new"
  // option beside it would offer the same outcome twice.
  const offerNew = fallback.kind !== 'kit_new_sku' || choice === 'new';
  // Offered while the list loads and whenever there is something to pick. A
  // Company with nothing on file is not offered a list it would find empty.
  const offerExisting = !statusLoaded || skus.length > 0 || choice === 'existing';
  // The other items this one can be part of, in page order. Only saved ones: an
  // unsaved line's id is replaced by the save, so a pick of it would name nothing.
  // An item that goes into this one, directly or along a chain, is left out too,
  // since the gate refuses the loop. Offered on prints and assemblies alike,
  // but not on a print a kit consumes or beside a default that already says which
  // item it is part of.
  const hostItems = items.filter(
    (i) =>
      i.id !== item.id &&
      i.saved &&
      isConfiguredLine(i.config) &&
      !fulfillmentHostLeadsBack(item.id, i.id, lines),
  );
  const unsavedHosts = items.some((i) => i.id !== item.id && !i.saved && isConfiguredLine(i.config));
  const offerWith =
    choice === 'with_item' ||
    (fallback.kind !== 'into_kit' && fallback.kind !== 'with_item' && (hostItems.length > 0 || unsavedHosts));

  function pickChoice(next: RowChoice): void {
    if (next === 'unset') return;
    if (next === '') {
      patch(item.id, undefined);
    } else if (next === 'existing') {
      patch(item.id, { choice: 'existing' });
    } else if (next === 'with_item') {
      const only = hostItems.length === 1 ? hostItems[0] : undefined;
      patch(item.id, { choice: 'with_item', ...(only ? { with_line_id: only.id } : {}) });
    } else {
      patch(item.id, {
        choice: 'new',
        ...(item.suggestedSkuName ? { new_sku_name: item.suggestedSkuName } : {}),
      });
    }
  }

  // Picking an existing SKU stores the sku string (the selection key Nexus
  // echoes to the warehouse) plus a display snapshot, so the row keeps reading
  // correctly if the catalog later changes.
  function pickSku(sku: string): void {
    const picked = sku ? skus.find((s) => s.sku === sku) : undefined;
    patch(
      item.id,
      picked
        ? { choice: 'existing', sku: picked.sku, sku_snapshot: fulfilledSkuSnapshot(picked) }
        : { choice: 'existing' },
    );
  }

  const pickedSku =
    stored?.choice === 'existing'
      ? (stored.sku ?? '')
      : resolved.kind === 'existing_sku'
        ? resolved.sku
        : '';
  const pickedHost = stored?.choice === 'with_item' ? (stored.with_line_id ?? '') : '';
  const typedName =
    stored?.choice === 'new'
      ? (stored.new_sku_name ?? '')
      : resolved.kind === 'new_sku'
        ? resolved.new_sku_name
        : '';

  return (
    <li className="fulfillment-target">
      <div className="fulfillment-target__item">
        {caption}
        <label htmlFor={choiceId} className="fulfillment-target__name">
          {item.label}
          {!defaultOption && <span className="required-marker"> *</span>}
        </label>
      </div>
      <div className="fulfillment-target__control">
        <select
          id={choiceId}
          value={choice}
          onChange={(e) => pickChoice(e.target.value as RowChoice)}
          required={!defaultOption}
          aria-invalid={choiceError ? true : undefined}
          aria-describedby={choiceError ? `${choiceId}-error` : undefined}
        >
          {choice === 'unset' && (
            <option value="unset" disabled>
              {t('orderServices.fulfillment.targets.choose')}
            </option>
          )}
          {defaultOption && <option value="">{defaultOption}</option>}
          {offerExisting && <option value="existing">{choiceLabels.existing}</option>}
          {offerNew && <option value="new">{choiceLabels.new}</option>}
          {offerWith && <option value="with_item">{choiceLabels.with_item}</option>}
        </select>
        {choiceError && (
          <span className="field-error" id={`${choiceId}-error`}>
            {choiceError}
          </span>
        )}
        {choice === 'existing' && (
          <>
            <label htmlFor={skuId} className="fulfillment-target__sub">
              {t('orderServices.fulfillment.skuLabel')}
              <span className="visually-hidden"> {item.label}</span>
            </label>
            {/* The same control the stock pickers use, over a DIFFERENT list on
                purpose: this asks which finished good the item goes INTO, and
                the answer is the set Argo already fulfils for this Company
                (company-fulfillment-status), not everything they own. An
                assembly's own kit SKU is its default, so it is not offered
                again as a remap. */}
            <SearchableSelect
              id={skuId}
              value={pickedSku}
              options={skus
                .filter((sk) => sk.sku !== ownKitSku)
                .map((sk) => fulfilledSkuOption(sk, t))}
              emptyOptionLabel={t('orderServices.fulfillment.skuEmptyOption')}
              searchPlaceholder={t('orderServices.fulfillment.skuSearchPlaceholder')}
              noMatchLabel={t('orderServices.fulfillment.skuNoMatch')}
              onChange={pickSku}
              refresh={skuRefresh}
              loading={skuRefresh?.busy === true}
              invalid={!!skuError}
              {...(skuError ? { describedBy: `${skuId}-error` } : {})}
            />
            {statusLoaded && skus.length === 0 && (
              <span className="help help--block">
                {t('orderServices.fulfillment.skuNoneOnFile')}
              </span>
            )}
            {skuError && (
              <span className="field-error" id={`${skuId}-error`}>
                {skuError}
              </span>
            )}
          </>
        )}
        {choice === 'with_item' && (
          <>
            <label htmlFor={withId} className="fulfillment-target__sub">
              {t('orderServices.fulfillment.withItemLabel')}
              <span className="visually-hidden"> {item.label}</span>
            </label>
            <select
              id={withId}
              value={pickedHost}
              onChange={(e) =>
                patch(item.id, {
                  choice: 'with_item',
                  ...(e.target.value ? { with_line_id: e.target.value } : {}),
                })
              }
              aria-invalid={withError ? true : undefined}
              aria-describedby={withError ? `${withId}-error` : undefined}
            >
              <option value="">{t('orderServices.fulfillment.targets.choose')}</option>
              {hostItems.map((host) => (
                <option key={host.id} value={host.id}>
                  {host.label}
                </option>
              ))}
            </select>
            {unsavedHosts && (
              <span className="help help--block">{t('spec.inserts.unsavedNote')}</span>
            )}
            {withError && (
              <span className="field-error" id={`${withId}-error`}>
                {withError}
              </span>
            )}
          </>
        )}
        {choice === 'new' && (
          <>
            <label htmlFor={nameId} className="fulfillment-target__sub">
              {t('orderServices.fulfillment.newSkuLabel')}
              <span className="visually-hidden"> {item.label}</span>
            </label>
            <input
              id={nameId}
              type="text"
              value={typedName}
              placeholder={t('orderServices.fulfillment.newSkuPlaceholder')}
              onChange={(e) => patch(item.id, { choice: 'new', new_sku_name: e.target.value })}
              aria-invalid={nameError ? true : undefined}
              aria-describedby={nameError ? `${nameId}-error` : undefined}
            />
            {nameError && (
              <span className="field-error" id={`${nameId}-error`}>
                {nameError}
              </span>
            )}
          </>
        )}
      </div>
    </li>
  );
}

function withoutTarget(config: unknown): unknown {
  if (typeof config !== 'object' || config === null || Array.isArray(config)) return config;
  return withLineFulfillmentTarget(config as Record<string, unknown>, undefined);
}
